import React from "react";
import { Box, Card, CardContent, Stack, Typography, SvgIconProps } from "@mui/material";
import { alpha } from "@mui/material/styles";

interface StatCardProps {
  icon: React.ComponentType<SvgIconProps>;
  label: string;
  value: string | number;
  testId?: string;
}

const fadeInUp = {
  '@keyframes fadeInUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(4px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
};

export const StatCard: React.FC<StatCardProps> = ({ icon: Icon, label, value, testId }) => {
  return (
    <Card
      sx={{
        width: "100%",
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "10px",
        boxShadow: 1,
        position: "relative",
        overflow: "hidden",
        transition: "transform 0.2s ease-in-out, box-shadow 0.15s ease",
        ...fadeInUp,
        animation: 'fadeInUp 150ms ease forwards',
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          bgcolor: "primary.main",
          borderRadius: "10px 10px 0 0",
          zIndex: 2,
        },
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: 3,
        },
      }}
    >
      {/* Gradient fade underneath accent stripe */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "8px",
          background: (theme) => `linear-gradient(to bottom, ${alpha(theme.palette.primary.main, 0.14)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 20%, transparent 100%)`,
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <CardContent sx={{ px: 3.5, py: 3, position: "relative", zIndex: 1 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.10),
              color: "primary.main",
            }}
          >
            <Icon sx={{ fontSize: 24, color: "inherit" }} />
          </Box>
          <Stack spacing={0.5}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: "text.secondary",
                lineHeight: 1.2,
              }}
            >
              {label}
            </Typography>
            <Typography
              sx={{
                fontSize: "1.75rem",
                fontWeight: 700,
                lineHeight: 1.1,
                display: "block",
                color: "text.primary",
              }}
              data-testid={testId}
            >
              {value}
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};
